import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { createAssetClassFilterFields } from "@/features/FilterTable/assetClassFilterFields";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { createCurrencyOption } from "@/hooks/useCurrencyOption";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";
import { z } from "zod";
import { formFields } from "./edit_card";
import { DEFAULT_ASSET_CLASS_OPTIONS } from "../A6StaticData/edit-form-conf";
import { useTranslation } from "react-i18next";

const filterA6TimeSeriesSchema = z.object({
  a6_static_datum_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  isin_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  currency_contains: z.string().optional(),
  date_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  date_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  asset_class_1_equals: z.string().optional(),
  asset_class_2_equals: z.string().optional(),
});

const filterA6TimeSeriesFields = filterA6TimeSeriesSchema.keyof().enum;

export type FilterA6TimeSeriesSchema = z.infer<typeof filterA6TimeSeriesSchema>;

const assetDepFields: readonly DepField<FilterA6TimeSeriesSchema>[] = [
  {
    name: filterA6TimeSeriesFields.a6_static_datum_name_contains,
    key: "id",
    extract: "id",
    bucket: "equals",
  },
  {
    name: filterA6TimeSeriesFields.isin_contains,
    key: "isin",
    extract: "text",
    bucket: "equals",
  },
];

export function FilterA6TimeSeries() {
  const { t, i18n } = useTranslation("tables");
  const tableName = Tables.A6_TIME_SERIES;

  const fields: Field[] = [
    {
      name: filterA6TimeSeriesFields.a6_static_datum_name_contains,
      label: t(`${tableName}.searchbox.a6_static_datum_name_contains`, { defaultValue: "Asset" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.A6}/${Tables.A6_STATIC_DATA}`,
      id_column: formFields.a6_static_datum_id,
      column_name: "name",
      col_span: 2,
      filters: makeFiltersBuilder<FilterA6TimeSeriesSchema>(
        assetDepFields,
        filterA6TimeSeriesFields.a6_static_datum_name_contains,
      ),
    },
    ...createAssetClassFilterFields({
      assetClassFieldName: filterA6TimeSeriesFields.asset_class_1_equals,
      assetSubclassFieldName: filterA6TimeSeriesFields.asset_class_2_equals,
      assetClassLabel: t(`${Tables.A6_TIME_SERIES}.filter.asset_class_1`),
      assetSubclassLabel: t(`${Tables.A6_TIME_SERIES}.filter.asset_class_2`),
      client: Clients.A6,
      staticDataTableName: Tables.A6_STATIC_DATA,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      assetSubclassOptions: DEFAULT_ASSET_CLASS_OPTIONS,
    }),
    {
      name: filterA6TimeSeriesFields.isin_contains,
      label: t(`${tableName}.searchbox.isin_contains`, { defaultValue: "ISIN" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.A6}/${Tables.A6_STATIC_DATA}`,
      id_column: formFields.a6_static_datum_id,
      column_name: "isin",
      filters: makeFiltersBuilder<FilterA6TimeSeriesSchema>(
        assetDepFields,
        filterA6TimeSeriesFields.isin_contains,
      ),
    },
    {
      name: filterA6TimeSeriesFields.currency_contains,
      label: t(`${tableName}.searchbox.currency_contains`, { defaultValue: "Currency" }),
      type: "autocomplete",
      options: createCurrencyOption(),
    },
    {
      name: "date",
      type: "dateRange",
      label: t(`${tableName}.searchbox.date`, { defaultValue: "Value date" }),
      from: { name: filterA6TimeSeriesFields.date_from },
      to: { name: filterA6TimeSeriesFields.date_to },
      col_span: 2,
    },
    {
      name: "create",
      type: "dateRange",
      label: t(`${tableName}.searchbox.create`, { defaultValue: "Creation date" }),
      from: { name: filterA6TimeSeriesFields.create_from },
      to: { name: filterA6TimeSeriesFields.create_to },
      col_span: 2,
    },
  ];

  return (
    <FilterTable
      schema={filterA6TimeSeriesSchema}
      client={Clients.A6}
      tableName={Tables.A6_TIME_SERIES}
      fields={fields}
      columns={2}
    />
  );
}
